import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmployeePageLayout } from "../core/EmployeePageLayout";
import { EmployeeProfileHeader } from "./EmployeeProfileHeader";
import { EmployeeProfileTabs } from "./EmployeeProfileTabs";
import { ComplianceBadge } from "./ComplianceBadge";

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  hire_date: string;
  is_active: boolean;
  role: 'staff' | 'admin';
  created_at: string;
}

interface EmployeeProfile {
  id: string;
  hourly_rate: number | null;
  salary_type: string | null;
  overtime_rate: number | null;
  payment_method: string | null;
  avatar_url: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relation: string | null;
  medical_notes: string | null;
}

interface TimeEntry {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
  notes: string | null;
}

interface PayrollCalculation {
  id: string;
  period_start: string;
  period_end: string;
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  gross_pay: number;
  net_pay: number;
  status: string;
  created_at: string;
}

interface Document {
  id: string;
  name: string;
  file_url: string;
  category: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  expires_at: string | null;
  is_required: boolean | null;
  uploaded_by: string | null;
}

interface Compliance {
  emp_id: string;
  compliance_status: string;
  missing_documents: number;
  expired_documents: number;
  expiring_soon_documents: number;
  total_required_documents: number;
  uploaded_documents: number;
}

interface EmployeeData {
  employee: Employee;
  profile: EmployeeProfile | null;
  timeEntries: TimeEntry[];
  payrollHistory: PayrollCalculation[];
  documents: Document[];
  compliance: Compliance | null;
}

export const EmployeeProfile: React.FC = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams<{ employeeId: string }>();
  const [data, setData] = useState<EmployeeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeData();
    }
  }, [employeeId]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      
      // Fetch employee basic info
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .eq('id', employeeId)
        .single();

      if (employeeError) throw employeeError;

      // Fetch employee profile
      const { data: profileData } = await supabase
        .from('employee_profiles')
        .select('*')
        .eq('employee_id', employeeId)
        .single();

      // Fetch recent time entries (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: timeData } = await supabase
        .from('time_entries')
        .select('*')
        .eq('employee_id', employeeId)
        .gte('clock_in_time', thirtyDaysAgo.toISOString())
        .order('clock_in_time', { ascending: false })
        .limit(20);

      // Fetch payroll history
      const { data: payrollData } = await supabase
        .from('payroll_calculations')
        .select('*')
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false })
        .limit(10);

      // Fetch documents
      const { data: documentsData } = await supabase
        .from('employee_documents')
        .select('*')
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false });

      // Fetch compliance status
      const { data: complianceData } = await supabase
        .from('employee_compliance_summary')
        .select('*')
        .eq('emp_id', employeeId)
        .single();

      setData({
        employee: employeeData,
        profile: profileData,
        timeEntries: timeData || [],
        payrollHistory: payrollData || [],
        documents: documentsData || [],
        compliance: complianceData || null
      });

    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/?view=employee-management');
  };

  if (loading) {
    return (
      <EmployeePageLayout
        title="Loading..."
        subtitle="Please wait while we load the employee profile"
        onBack={handleBack}
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </EmployeePageLayout>
    );
  }

  if (!data?.employee) {
    return (
      <EmployeePageLayout
        title="Employee Not Found"
        subtitle="The requested employee could not be found"
        onBack={handleBack}
      >
        <div className="text-center py-8">
          <p className="text-muted-foreground">This employee may have been deleted or the ID is incorrect.</p>
        </div>
      </EmployeePageLayout>
    );
  }

  const titleWithCompliance = (
    <div className="flex items-center gap-3">
      <span>{`${data.employee.first_name} ${data.employee.last_name}`}</span>
      {data.compliance && (
        <ComplianceBadge
          status={data.compliance.compliance_status as any}
          missingCount={data.compliance.missing_documents}
          expiredCount={data.compliance.expired_documents}
          expiringSoonCount={data.compliance.expiring_soon_documents}
        />
      )}
    </div>
  );

  return (
    <EmployeePageLayout
      title={`${data.employee.first_name} ${data.employee.last_name}`}
      subtitle={`Employee ID: ${data.employee.employee_id}`}
      onBack={handleBack}
    >
      <div className="space-y-6">
        {data.compliance && (
          <div className="flex items-center gap-3">
            <span className="text-lg font-medium">Compliance Status:</span>
            <ComplianceBadge
              status={data.compliance.compliance_status as any}
              missingCount={data.compliance.missing_documents}
              expiredCount={data.compliance.expired_documents}
              expiringSoonCount={data.compliance.expiring_soon_documents}
            />
          </div>
        )}
        
        <EmployeeProfileHeader
          employee={data.employee}
          profile={data.profile}
          timeEntries={data.timeEntries}
          payrollHistory={data.payrollHistory}
          onDataUpdate={fetchEmployeeData}
        />
        
        <EmployeeProfileTabs
          employee={data.employee}
          profile={data.profile}
          timeEntries={data.timeEntries}
          payrollHistory={data.payrollHistory}
          documents={data.documents}
          onDataUpdate={fetchEmployeeData}
        />
      </div>
    </EmployeePageLayout>
  );
};